import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Avatar, EmptyState, Spinner } from '../components/ui.jsx';
import { api } from '../api/client.js';
import { useAuth } from '../state/AuthContext.jsx';
import { useToast } from '../state/ToastContext.jsx';
import { useTitle } from '../lib/hooks.js';
import { formatDateTime, formatXaf, timeAgo } from '../lib/format.js';

// Chat over plain polling. There is no websocket server yet, so the open thread
// is refetched every ten seconds while the tab is in the foreground. Everything
// else about the interface stays the same when sockets arrive later.
const POLL_MS = 10_000;

const Messages = () => {
  useTitle('Messages');
  const { user, setCounts } = useAuth();
  const toast = useToast();
  const [params, setParams] = useSearchParams();

  const [threads, setThreads] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [thread, setThread] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const bottom = useRef(null);

  const propertyId = params.get('propertyId');
  const withUser = params.get('withUser');

  const loadThreads = useCallback(async () => {
    try {
      const data = await api.get('/messages/threads');
      setThreads(data.items);
      return data.items;
    } catch (error) {
      toast.error(error.message);
      return [];
    } finally {
      setLoadingThreads(false);
    }
  }, [toast]);

  useEffect(() => {
    loadThreads().then((items) => {
      // Land on the most recent conversation so the panel is never empty.
      if (!propertyId && items.length > 0) {
        setParams({ propertyId: String(items[0].propertyId), withUser: String(items[0].other.id) }, { replace: true });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadThread = useCallback(
    async ({ quiet } = {}) => {
      if (!propertyId || !withUser) {
        setThread(null);
        return;
      }
      if (!quiet) setLoadingThread(true);
      try {
        const data = await api.get(`/messages/thread?propertyId=${propertyId}&withUser=${withUser}`);
        setThread(data);
        // Opening the thread clears its unread count on the server.
        setThreads((current) =>
          current.map((item) =>
            item.propertyId === Number(propertyId) && item.other.id === Number(withUser)
              ? { ...item, unread: 0 }
              : item
          )
        );
      } catch (error) {
        if (!quiet) toast.error(error.message);
      } finally {
        setLoadingThread(false);
      }
    },
    [propertyId, withUser, toast]
  );

  useEffect(() => {
    loadThread();
  }, [loadThread]);

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') loadThread({ quiet: true });
    }, POLL_MS);
    return () => clearInterval(id);
  }, [loadThread]);

  // Refresh the header badge whenever the open thread changes.
  useEffect(() => {
    if (!thread) return;
    api
      .get('/messages/unread-count')
      .then((data) => setCounts((current) => ({ ...current, messages: data.count })))
      .catch(() => {});
  }, [thread, setCounts]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [thread?.messages?.length]);

  const send = async (event) => {
    event.preventDefault();
    const body = draft.trim();
    if (!body) return;

    setSending(true);
    try {
      const { message } = await api.post('/messages', {
        propertyId: Number(propertyId),
        receiverId: Number(withUser),
        body
      });
      setDraft('');
      setThread((current) => (current ? { ...current, messages: [...current.messages, message] } : current));
      loadThreads();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSending(false);
    }
  };

  const grouped = useMemo(() => {
    // Insert a date divider whenever the day changes.
    const output = [];
    let lastDay = null;
    for (const message of thread?.messages || []) {
      const day = (message.createdAt || '').slice(0, 10);
      if (day !== lastDay) {
        output.push({ divider: day, id: `divider-${day}` });
        lastDay = day;
      }
      output.push(message);
    }
    return output;
  }, [thread]);

  if (loadingThreads) return <Spinner />;

  if (threads.length === 0) {
    return (
      <div className="container section">
        <EmptyState
          title="No conversations yet"
          message={
            user.role === 'landlord'
              ? 'When a tenant asks about one of your listings, the conversation will appear here.'
              : 'Open a listing and use "Message the landlord" to start a conversation.'
          }
          action={
            <Link to={user.role === 'landlord' ? '/landlord/properties' : '/search'} className="btn btn-primary">
              {user.role === 'landlord' ? 'See my listings' : 'Browse listings'}
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="container section">
      <h1 className="page-title">Messages</h1>

      <div className="chat-layout">
        <aside className="thread-list" aria-label="Conversations">
          {threads.map((item) => {
            const active = item.propertyId === Number(propertyId) && item.other.id === Number(withUser);
            return (
              <button
                key={`${item.propertyId}-${item.other.id}`}
                type="button"
                className={`thread-item ${active ? 'is-current' : ''} ${item.unread ? 'has-unread' : ''}`}
                onClick={() => setParams({ propertyId: String(item.propertyId), withUser: String(item.other.id) })}
              >
                <Avatar name={item.other.name} url={item.other.avatarUrl} size={40} />
                <span className="thread-text">
                  <strong>{item.other.name}</strong>
                  <em>{item.property?.title || 'Listing removed'}</em>
                  <span className="thread-preview">{item.lastMessage?.body || ''}</span>
                </span>
                <span className="thread-meta">
                  <span>{timeAgo(item.lastAt)}</span>
                  {item.unread ? <span className="thread-badge">{item.unread}</span> : null}
                </span>
              </button>
            );
          })}
        </aside>

        <section className="chat-panel">
          {loadingThread && !thread ? <Spinner /> : null}

          {thread ? (
            <>
              <header className="chat-head">
                <div>
                  <strong>{thread.other.name}</strong>
                  {thread.property ? (
                    <span className="muted">
                      <Link to={`/properties/${thread.property.id}`}>{thread.property.title}</Link> -{' '}
                      {formatXaf(thread.property.price)} / month
                    </span>
                  ) : (
                    <span className="muted">This listing has been removed</span>
                  )}
                </div>
              </header>

              <div className="chat-scroll">
                {grouped.map((entry) =>
                  entry.divider ? (
                    <div key={entry.id} className="chat-divider">
                      <span>{formatDateTime(`${entry.divider} 12:00:00`).split(',')[0]}</span>
                    </div>
                  ) : (
                    <div
                      key={entry.id}
                      className={`bubble ${entry.senderId === user.id ? 'is-mine' : 'is-theirs'}`}
                    >
                      <p>{entry.body}</p>
                      <time>{formatDateTime(entry.createdAt)}</time>
                    </div>
                  )
                )}
                <div ref={bottom} />
              </div>

              <form className="chat-composer" onSubmit={send}>
                <textarea
                  rows="2"
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    // Enter sends, shift+enter makes a new line.
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      send(event);
                    }
                  }}
                  placeholder="Write a message"
                />
                <button type="submit" className="btn btn-primary" disabled={sending || !draft.trim()}>
                  {sending ? 'Sending' : 'Send'}
                </button>
              </form>
            </>
          ) : (
            <EmptyState title="Pick a conversation" message="Choose one from the list to read it." />
          )}
        </section>
      </div>
    </div>
  );
};

export default Messages;
